<template>
  <div class="c-pairs-table">
    <div class="c-pairs-table__toolbar">
      <div class="ui-field c-pairs-table__search">
        <div class="ui-field__control ui-field__control--with-action">
          <app-icon
            class="ui-field__icon"
            name="search"
          />
          <input
            :value="searchQuery"
            class="ui-field__input"
            type="text"
            placeholder="Search events by name or ID…"
            aria-label="Search events"
            @input="updateSearch($event.target.value)"
          >
          <button
            v-if="searchQuery"
            type="button"
            class="ui-field__action"
            title="Clear search"
            aria-label="Clear search"
            @click="updateSearch('')"
          >
            <app-icon name="close" />
          </button>
        </div>
      </div>

      <p
        class="c-pairs-table__summary"
        aria-live="polite"
      >
        <template v-if="searchQuery">
          <strong>{{ rows.length }}</strong> of {{ total }} events
        </template>
        <template v-else>
          <strong>{{ rows.length }}</strong> {{ rows.length === 1 ? 'event' : 'events' }}
          <span
            v-if="unpairedCount"
            class="c-pairs-table__summary-unpaired"
          >· {{ unpairedCount }} not paired</span>
        </template>
      </p>
    </div>

    <table v-if="rows.length">
      <thead>
        <tr>
          <th
            v-for="column in columns"
            :key="column.key"
            :class="[`c-pairs-table__col--${column.key}`, { 'is-sortable': column.sortBy, 'is-sorted': column.sortBy === sortBy }]"
            :aria-sort="ariaSort(column)"
          >
            <button
              v-if="column.sortBy"
              type="button"
              class="c-pairs-table__sort"
              @click="toggleSort(column.sortBy)"
            >
              {{ column.label }}
              <app-icon
                class="c-pairs-table__sort-icon"
                :name="sortIcon(column)"
                :size="16"
              />
            </button>
            <span v-else>{{ column.label }}</span>
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="(row, index) in sortedRows"
          :key="`${row.rowData.streamId}-${index}`"
          :class="{ 'is-unpaired': !isPaired(row) }"
        >
          <td>
            <div class="c-pairs-table__event">
              <span class="c-pairs-table__name">{{ row.rowData.streamEvent }}</span>
              <span class="c-pairs-table__meta">
                <span class="c-pairs-table__id">ID {{ row.rowData.streamId || '—' }}</span>
                <span
                  v-if="row.rowData.eventStatusCode === 'O'"
                  class="c-pairs-table__badge c-pairs-table__badge--info"
                >
                  <app-icon
                    name="ondemand_video"
                    :size="14"
                  />
                  On demand
                </span>
              </span>
            </div>
          </td>

          <td class="c-pairs-table__link-cell">
            <app-icon
              :name="isPaired(row) ? 'link' : 'link_off'"
              :size="18"
            />
          </td>

          <td>
            <div
              v-if="isPaired(row)"
              class="c-pairs-table__event"
            >
              <span class="c-pairs-table__name">{{ row.rowData.feedEvent }}</span>
              <span class="c-pairs-table__meta">
                <span class="c-pairs-table__id">ID {{ row.rowData.feedId }}</span>
              </span>
            </div>
            <span
              v-else
              class="c-pairs-table__badge c-pairs-table__badge--neutral"
            >
              Not paired
            </span>
          </td>

          <td>
            <div class="c-pairs-table__time">
              <span class="c-pairs-table__time-hour">{{ splitStartTime(row).time }}</span>
              <span class="c-pairs-table__time-date">{{ splitStartTime(row).date }}</span>
            </div>
          </td>

          <td>
            <div
              v-if="row.rowData.matchIndex === 'Manual'"
              class="c-pairs-table__badge c-pairs-table__badge--manual"
            >
              <app-icon
                name="back_hand"
                :size="14"
              />
              Manual
            </div>
            <div
              v-else-if="matchPercent(row) !== null"
              class="c-pairs-table__match"
              :class="`c-pairs-table__match--${matchLevel(row)}`"
            >
              <span class="c-pairs-table__match-value">{{ row.rowData.matchIndex }}</span>
              <span class="c-pairs-table__match-bar">
                <span :style="{ width: `${matchPercent(row)}%` }" />
              </span>
            </div>
            <span
              v-else
              class="c-pairs-table__muted"
            >—</span>
          </td>

          <td class="c-pairs-table__actions">
            <button
              type="button"
              class="c-pairs-table__action"
              title="Edit pairing"
              aria-label="Edit pairing"
              @click="$emit('edit', row.eventData)"
            >
              <app-icon
                name="edit"
                :size="18"
              />
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <div
      v-else
      class="c-table__empty"
    >
      <span class="c-table__empty-icon">
        <app-icon
          name="event_busy"
          :size="24"
        />
      </span>
      <template v-if="searchQuery">
        <p class="c-table__empty-title">
          No events match “{{ searchQuery }}”
        </p>
        <p>Try another name or ID, or clear the search.</p>
        <button
          type="button"
          class="ui-btn ui-btn--secondary c-pairs-table__clear"
          @click="updateSearch('')"
        >
          Clear search
        </button>
      </template>
      <template v-else>
        <p class="c-table__empty-title">
          No events found
        </p>
        <p>There are no events for this sport right now.</p>
      </template>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import AppIcon from '@/shared/app-icon/AppIcon';

// Shared table for IMG Arena / Infront / Stats Perform event pairs.
// rows: [{ eventData, rowData: { streamEvent, streamId, feedEvent, feedId, startTime, matchIndex, eventStatusCode? } }]
// startTime is "DD/MM/YYYY HH:mm"; matchIndex is "95.52%", "Manual" or "-".
const START_TIME_FORMAT = 'DD/MM/YYYY HH:mm';

const SORT_VALUE = {
  'rowData.streamEvent': row => String(row.rowData.streamEvent || '').toLowerCase(),
  // unpaired rows sort after paired ones
  'rowData.feedEvent': row => (row.rowData.feedEvent && row.rowData.feedEvent !== '-'
    ? row.rowData.feedEvent.toLowerCase()
    : '\uffff'),
  'rowData.startTime': row => moment(row.rowData.startTime, START_TIME_FORMAT).valueOf() || 0,
  // manual pairings rank above any automatic match
  'rowData.matchIndex': row => (row.rowData.matchIndex === 'Manual' ? 101 : parseFloat(row.rowData.matchIndex) || -1),
};
export default {
  name: 'EventPairsTable',
  components: {
    AppIcon,
  },
  props: {
    rows: {
      type: Array,
      required: true,
    },
    // .sync-able search term; the parent does the filtering
    searchQuery: {
      type: String,
      default: '',
    },
    // number of events before search filtering
    total: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    // earliest event first
    // no column sort by default: rows keep the page's order (manual, paired, unpaired)
    sortBy: null,
    sortOrder: 'asc',
    columns: [
      { key: 'stream', label: 'Stream event', sortBy: 'rowData.streamEvent' },
      { key: 'link', label: '' },
      { key: 'feed', label: 'Paired feed event', sortBy: 'rowData.feedEvent' },
      { key: 'start', label: 'Start', sortBy: 'rowData.startTime' },
      { key: 'match', label: 'Match', sortBy: 'rowData.matchIndex' },
      { key: 'actions', label: '' },
    ],
  }),
  computed: {
    unpairedCount() {
      return this.rows.filter(row => !this.isPaired(row)).length;
    },

    sortedRows() {
      const getValue = SORT_VALUE[this.sortBy];

      if (!getValue) {
        return this.rows;
      }

      const direction = this.sortOrder === 'asc' ? 1 : -1;

      return [...this.rows].sort((a, b) => {
        const aValue = getValue(a);
        const bValue = getValue(b);

        if (typeof aValue === 'string') {
          return aValue.localeCompare(bValue) * direction;
        }

        return (aValue - bValue) * direction;
      });
    },
  },
  methods: {
    updateSearch(value) {
      this.$emit('update:searchQuery', value);
      this.$emit('search', value);
    },

    isPaired(row) {
      return row.rowData.matchIndex === 'Manual' || (row.rowData.feedEvent && row.rowData.feedEvent !== '-');
    },

    // "09/10/2026 15:30" -> { date: "09/10/2026", time: "15:30" }
    splitStartTime(row) {
      const [date = '', time = ''] = String(row.rowData.startTime || '').split(' ');

      return { date, time };
    },

    matchPercent(row) {
      const value = parseFloat(row.rowData.matchIndex);

      return Number.isNaN(value) ? null : Math.max(0, Math.min(100, value));
    },

    matchLevel(row) {
      const value = this.matchPercent(row);

      if (value >= 90) {
        return 'high';
      }

      return value >= 70 ? 'medium' : 'low';
    },

    toggleSort(sortBy) {
      this.sortOrder = sortBy === this.sortBy && this.sortOrder === 'asc' ? 'desc' : 'asc';
      this.sortBy = sortBy;
    },

    sortIcon(column) {
      if (column.sortBy !== this.sortBy) {
        return 'unfold_more';
      }

      return this.sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward';
    },

    ariaSort(column) {
      if (!column.sortBy || column.sortBy !== this.sortBy) {
        return null;
      }

      return this.sortOrder === 'asc' ? 'ascending' : 'descending';
    },
  },
};
</script>

<style lang="scss" scoped>
.c-pairs-table {
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background-color: var(--bg-surface);
  font-family: var(--font-sans);
  // clip the rounded corners without breaking the sticky header
  overflow: clip;

  table {
    width: 100%;
    // fixed widths so columns don't shift while searching / sorting
    table-layout: fixed;
    border-collapse: separate;
    border-spacing: 0;
  }

  th,
  td {
    padding: 12px 16px;
    text-align: left;
    vertical-align: middle;
  }

  th {
    position: sticky;
    top: -20px; // the scroll panel has 20px top padding
    z-index: $zIndexStickyTop;
    height: 44px;
    padding-top: 0;
    padding-bottom: 0;
    background-color: var(--bg-subtle);
    border-bottom: 1px solid var(--border-default);
    color: var(--text-secondary);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    white-space: nowrap;
  }

  td {
    border-bottom: 1px solid var(--border-default);
    font-size: 14px;
    color: var(--text-primary);
  }

  tbody tr {
    transition: background-color var(--duration-fast) ease;

    &:last-child td {
      border-bottom: none;
    }

    &:hover {
      background-color: var(--bg-subtle);

      .c-pairs-table__action {
        color: var(--text-primary);
      }
    }

    &.is-unpaired td:first-child {
      box-shadow: inset 3px 0 0 var(--border-strong);
    }
  }
}

// ---------- toolbar ----------
.c-pairs-table__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-default);
}

.c-pairs-table__search {
  flex: 1 1 280px;
  max-width: 420px;

  .ui-field__input {
    height: 38px;
    font-size: 14px;
  }
}

.c-pairs-table__summary {
  margin: 0;
  font-size: 13px;
  color: var(--text-secondary);
  white-space: nowrap;

  strong {
    font-weight: 600;
    color: var(--text-primary);
  }
}

.c-pairs-table__summary-unpaired {
  color: var(--text-muted);
}

.c-pairs-table__clear {
  margin-top: 12px;
}

.c-pairs-table__col--link,
.c-pairs-table__col--actions {
  width: 56px;
}

.c-pairs-table__col--start {
  width: 120px;
}

.c-pairs-table__col--match {
  width: 140px;
}

.c-pairs-table__name {
  overflow-wrap: anywhere;
}

// ---------- header sort ----------
.c-pairs-table__sort {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0 -6px;
  padding: 4px 6px;
  border-radius: var(--radius-sm);
  background: transparent;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  cursor: pointer;

  &:hover {
    background-color: var(--bg-hover);
    color: var(--text-primary);

    .c-pairs-table__sort-icon {
      opacity: 1;
    }
  }

  &:focus-visible {
    box-shadow: var(--focus-ring);
  }
}

.c-pairs-table__sort-icon {
  opacity: 0.45;
}

th.is-sorted {
  color: var(--text-primary);

  .c-pairs-table__sort-icon {
    opacity: 1;
    color: var(--brand-600);
  }
}

// ---------- cells ----------
.c-pairs-table__event {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.c-pairs-table__name {
  font-weight: 500;
  line-height: 1.35;
}

.c-pairs-table__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.c-pairs-table__id {
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 12px;
  color: var(--text-muted);
}

.c-pairs-table__link-cell {
  padding-left: 0 !important;
  padding-right: 0 !important;
  text-align: center !important;
  color: var(--text-muted);

  .is-unpaired & {
    color: var(--border-strong);
  }
}

.c-pairs-table__time {
  display: flex;
  flex-direction: column;
  gap: 2px;
  white-space: nowrap;
}

.c-pairs-table__time-hour {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.c-pairs-table__time-date {
  font-size: 12px;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
}

.c-pairs-table__muted {
  color: var(--text-muted);
}

// ---------- badges ----------
.c-pairs-table__badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  padding: 0 8px;
  border: 1px solid;
  border-radius: var(--radius-full);
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.c-pairs-table__badge--neutral {
  border-color: var(--border-default);
  background-color: var(--bg-subtle);
  color: var(--text-secondary);
}

.c-pairs-table__badge--info,
.c-pairs-table__badge--manual {
  border-color: var(--info-border);
  background-color: var(--info-bg);
  color: var(--info-fg);
}

// ---------- match index ----------
.c-pairs-table__match {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 88px;
}

.c-pairs-table__match-value {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.c-pairs-table__match-bar {
  height: 4px;
  border-radius: 4px;
  background-color: var(--bg-hover);
  overflow: hidden;

  span {
    display: block;
    height: 100%;
    border-radius: inherit;
    background-color: currentColor;
  }
}

.c-pairs-table__match--high {
  color: var(--success-fg);
}

.c-pairs-table__match--medium {
  color: var(--warning-fg);
}

.c-pairs-table__match--low {
  color: var(--danger-fg);
}

// ---------- actions ----------
.c-pairs-table__actions {
  text-align: right !important;
}

.c-pairs-table__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  transition: background-color var(--duration-fast) ease, color var(--duration-fast) ease;

  &:hover {
    background-color: var(--bg-hover);
    color: var(--brand-600) !important;
  }

  &:focus-visible {
    box-shadow: var(--focus-ring);
  }
}
</style>
